import React from 'react';
import { RegulatoryRequirement, RegulatoryDashboardStats } from '../types';

interface ComplianceCoverageTabProps {
  stats: RegulatoryDashboardStats | null;
  requirements: RegulatoryRequirement[];
  onSelectRequirement: (req: RegulatoryRequirement) => void;
}

export const ComplianceCoverageTab: React.FC<ComplianceCoverageTabProps> = ({
  stats,
  requirements,
  onSelectRequirement,
}) => {
  // Find gap requirements (applicable but no mapped controls)
  const gapRequirements = requirements.filter(
    (r) =>
      r.organizationApplicability?.applicabilityStatus === 'APPLICABLE' &&
      (!r.controlMappings || r.controlMappings.length === 0)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Coverage Overview Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Regulatory Coverage Ratio</div>
          <div className="stat-value" style={{ color: '#2563eb' }}>
            %{stats?.complianceCoveragePercentage || 0}
          </div>
          <div className="stat-sub">Internal control assurance index</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Mapped Internal Controls</div>
          <div className="stat-value" style={{ color: '#10b981' }}>
            {stats?.mappedToControlsCount || 0} / {stats?.totalApplicableRequirements || 0}
          </div>
          <div className="stat-sub">Manual & SOP operational control links</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Compliance Gaps Unmapped</div>
          <div className="stat-value" style={{ color: '#ef4444' }}>
            {gapRequirements.length}
          </div>
          <div className="stat-sub">Applicable requirements without mapped controls</div>
        </div>
      </div>

      {/* Gap Analysis Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Regulatory Gap Register</h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
            Applicable mandatory requirements awaiting internal manual or SOP procedure mapping
          </p>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Requirement Code</th>
                <th>Title & Statement</th>
                <th>Framework</th>
                <th style={{ textAlign: 'center' }}>Gap Status</th>
                <th style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {gapRequirements.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#10b981', fontWeight: 600 }}>
                    All applicable regulatory requirements have active internal control mappings (100% Coverage).
                  </td>
                </tr>
              ) : (
                gapRequirements.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#dc2626' }}>
                      {r.requirementCode}
                    </td>
                    <td style={{ maxWidth: '320px' }}>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title}</div>
                      <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.requirementText}
                      </div>
                    </td>
                    <td style={{ fontSize: '13px', color: '#475569' }}>
                      {r.frameworkCode}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="badge-pill badge-danger">
                        MISSING CONTROL
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={() => onSelectRequirement(r)}
                        className="btn-primary"
                        style={{ fontSize: '12px', padding: '4px 10px' }}
                      >
                        Map Control →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
